// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export { CaseStatus, TERMINAL_STATUSES, SLA_PAUSED_STATUSES, SubStatus, SUB_STATUS_TO_STATUS, VALID_TRANSITIONS, } from './status.js';
export { RequestType, ALL_REQUEST_TYPES, REQUEST_TYPE_LABELS, REQUEST_TYPES_OFFERED_ONLY_WHERE_DECLARED, REQUEST_TYPES_HANDLED_IN_LEGAL_MATTERS, isRequestTypeHandledInLegalMatters, PLATFORM_DEFAULT_RIGHTS_PERIODS, platformDefaultRightsPeriodDays, } from './request-types.js';
// The 249 officially assigned ISO 3166-1 alpha-2 codes — codes only, names
// rendered through `Intl.DisplayNames` at the point of display.
export { ISO_3166_1_ALPHA_2 } from './iso-3166-1-alpha-2.js';
// Sprint 64.32.0 — the closed provenance vocabulary for client_csv_export.
export { CLIENT_EXPORT_SOURCE_MODULES, isClientExportSourceModule, } from './client-export-source-modules.js';
export { Permission, SystemRole, ROLE_PERMISSIONS, CUSTOM_ONLY_PERMISSIONS, PLATFORM_ONLY_PERMISSIONS, 
// Fix sprint #610 (F6) — the delegation exclusions, one list for both sides.
DELEGATION_EXCLUDED_PERMISSIONS, isNonDelegablePermission, 
// Owner ruling 2026-10-22 (OR-9) — the writes only `platform_admin` holds.
PLATFORM_ADMIN_ROLE_ONLY_PERMISSIONS, 
// Fix sprint #447 (OR6) — the recipient reads, imported by seed-roles.ts.
STAFF_ACKNOWLEDGMENT_READ_PERMS, 
// Sprint 64.35.0 — the auditor suffix rule, exported so the backend seed and
// the release-gating contract test consume the SAME predicate the declared
// matrix is built from, rather than three drifting copies of it.
reachesAuditorBySuffix, } from './permissions.js';
export { AuditActionType, AML_CASE_AUDIT_EVENT_TYPES, AML_RESTRICTED_AUDIT_EVENT_TYPES, GROUP_AUDIT_EXCLUDED_EVENT_TYPES, ActorType, SystemActorId, EventSensitivity, 
// Sprint 43.0.0 — audit payload enrichments
PauseReasonCode, RejectionReasonCodeEnum, } from './audit.js';
/** Supported locales. */
export const Locale = {
    VI: 'vi-VN',
    EN: 'en-US',
    DA: 'da-DK',
    SV: 'sv-SE',
    NB: 'nb-NO',
    NN: 'nn-NO',
    DE: 'de-DE',
    FI: 'fi-FI',
    NL: 'nl-NL',
    FR: 'fr-FR',
    LB: 'lb-LU',
    GA: 'ga-IE',
    ES: 'es-ES',
    CA: 'ca-ES',
    EU: 'eu-ES',
    PT: 'pt-PT',
};
/** Locale options for language selector dropdowns. */
export const LOCALE_OPTIONS = [
    { value: Locale.EN, label: 'English' },
    { value: Locale.VI, label: 'Tiếng Việt' },
    { value: Locale.DA, label: 'Dansk' },
    { value: Locale.SV, label: 'Svenska' },
    { value: Locale.NB, label: 'Norsk bokmål' },
    { value: Locale.NN, label: 'Norsk nynorsk' },
    { value: Locale.DE, label: 'Deutsch' },
    { value: Locale.FI, label: 'Suomi' },
    { value: Locale.NL, label: 'Nederlands' },
    { value: Locale.FR, label: 'Français' },
    { value: Locale.LB, label: 'Lëtzebuergesch' },
    { value: Locale.GA, label: 'Gaeilge' },
    { value: Locale.ES, label: 'Español' },
    { value: Locale.CA, label: 'Català' },
    { value: Locale.EU, label: 'Euskara' },
    { value: Locale.PT, label: 'Português' },
];
/** Case source (intake channel). */
export const CaseSource = {
    ADMIN_MANUAL: 'admin_manual',
    PUBLIC_FORM: 'public_form',
    CMP_GATEWAY: 'cmp_gateway',
    AESIRX_CMP: 'aesirx_cmp',
    PHONE_RECEIVED: 'phone_received',
    POSTAL_RECEIVED: 'postal_received',
    EMAIL_RECEIVED: 'email_received',
    IN_PERSON: 'in_person',
    LEGACY_IMPORT: 'legacy_import',
};
/** Closure reasons for terminal statuses. */
export const ClosureReason = {
    FULFILLED: 'fulfilled',
    PARTIALLY_FULFILLED: 'partially_fulfilled',
    UNFOUNDED: 'unfounded',
    EXCESSIVE: 'excessive',
    WITHDRAWN: 'withdrawn',
    DUPLICATE: 'duplicate',
    IDENTITY_UNVERIFIED: 'identity_unverified',
};
/** Identity verification statuses. */
export const VerificationStatus = {
    NOT_REQUIRED: 'not_required',
    PENDING: 'pending',
    VERIFIED: 'verified',
    FAILED: 'failed',
};
/** Note types. */
export const NoteType = {
    COMMENT: 'comment',
    STATUS_CHANGE: 'status_change',
    SLA_EVENT: 'sla_event',
    VERIFICATION_EVENT: 'verification_event',
    SYSTEM: 'system',
};
/** Note visibility. */
export const NoteVisibility = {
    INTERNAL: 'internal',
};
// Sprint 18.0.0: Package Deployment & License Management
export { PackageTier, PACKAGE_DEFINITIONS, getPackageModules, isModuleInPackage, 
// Sprint 45.3.0: Help Center package-tier awareness
HELP_MODULE_TO_MODULE_ID, ALWAYS_VISIBLE_HELP_MODULES, ENTERPRISE_ONLY_HELP_MODULES, isHelpModuleVisibleInTier, } from './packages.js';
// Sprint 37.1.0: Canonical Field Registry
export { CANONICAL_FIELDS, CANONICAL_REGISTRY_VERSION, } from './canonical-fields.js';
// Sprint 37.1.0: Workflow Profiles
export { WORKFLOW_PROFILES, getWorkflowProfile, getProfileStatuses, isValidProfileTransition, } from './workflow-profiles.js';
// Sprint 47.4.4: Immutable Audit Trails credit-package pricing card
export { IAT_CREDIT_PACKAGES, IAT_CREDIT_PACKAGE_CODES, } from './iat-pricing.js';
// F17 (fix-sprint-20260503-2128) — single source of truth for the
// canonical Mongo collection backing each ParentRecordType.
// Sprint 62.10.0 — TENANT_KEY_BY_RECORD_TYPE alongside it, because assuming
// the tenant key is what broke attach_evidence for every record type.
// Sprint 64.122.0 — PARENT_RECORD_BINDINGS is the source; the two maps above
// are derived from it. A lock fires only if the collection, the tenant key and
// the id field agree, and the third was assumed to be `_id` for 15 record types
// whose owning module reads on a business id.
export { COLLECTION_BY_RECORD_TYPE, TENANT_KEY_BY_RECORD_TYPE, ID_FIELDS_BY_RECORD_TYPE, PARENT_RECORD_BINDINGS, } from './parent-record-collections.js';
// fix-sprint-20260619-0053-followup-2 (finding #9) — locale-keyed PDPL
// email + cover-letter template strings.
export { PDPL_EMAIL_BODY_GREETINGS, PDPL_EMAIL_BODY_COVER_LETTER_DIVIDER, PDPL_EMAIL_BODY_OPERATOR_NOTES_DIVIDER, PDPL_COVER_LETTER_LABELS, formatPdplEmailGreeting, } from './pdpl-email-templates.js';
// Sprint 62.0.6 — onboarding persona registry. Replaces the hand-maintained
// PERSONA_PATHS array that had drifted 19 personas behind the authored corpus.
export { PERSONA_REGISTRY, PERSONA_REGISTRY_BY_ID, ALL_PERSONA_IDS } from './persona-registry.js';
// Sprint 63.28.0 (A1, A2) — the role-family and sector vocabularies Persona
// Paths groups by, and the one map from a persona's destination key to a route
// and the sidebar's own label.
export { PERSONA_ROLE_FAMILIES, PERSONA_ROLE_FAMILY_KEYS, PERSONA_SECTORS, PERSONA_SECTOR_KEYS, } from './persona-taxonomy.js';
export { PERSONA_DESTINATIONS, PERSONA_DESTINATION_KEYS, personaTourModules, } from './persona-destinations.js';
// Framework → persona relevance is declared by the pack on the framework record
// (`persona_relevance`) and read with `resolvePersonaRelevance` from utils. The
// `FRAMEWORK_PERSONA_RELEVANCE` constant that projected it here was removed on
// 2026-09-25 — it was R-46 residue, one framework-code literal per row.
export { USE_CASE_PRIMARY_PERSONA, ALL_USE_CASE_IDS } from './use-case-registry.js';
// Sprint 64.0.0 — owner-type-aware attachment download policy
// Sprint 64.92.0 — resolved from the export KIND recorded on the artefact
export { OWNER_TYPE_DOWNLOAD_POLICY, OWNER_TYPE_DOWNLOAD_FALLBACK, EXPORT_KIND_DOWNLOAD_GATES, KINDED_EXPORT_OWNER_TYPES, requiredDownloadPermissions, isOwnerTypeDownloadPermitted, resolveDownloadGrant, recordedExportKind, kindedDownloadScopes, 
// Sprint 62.58.0 (gate E12.5 pass 3, P2-1) — restricted export sections
RESTRICTED_EXPORT_SECTION_GATES, SECTIONED_EXPORT_OWNER_TYPES, EXPORT_SECTION_SEPARATOR, sectionedExportKind, SECTIONED_EXPORT_TYPES, storedExportKind, restrictedSectionsOfKind, permittedRestrictedSections, 
// Sprint 63.3.0 — the WRITE analogue (idea-catalog #147)
OWNER_TYPE_WRITE_GRANT_OVERRIDES, 
// Sprint 62.54.0 — owner types decided apart from their shared read grant
OWNER_TYPE_WRITE_GRANT_BY_OWNER_TYPE, NO_WRITE_ANALOGUE, deriveOwnerTypeWriteGrant, isOwnerTypeWritePermitted, } from './attachment-download-policy.js';
export * from './pagination.js';
// Sprint 63.42.1 (OR-26) — which attachment owner types hold Sector Overlays content.
export { OVERLAY_ATTACHMENT_OWNER_TYPES, OVERLAY_CANDIDATE_OWNER_TYPES, overlayAttachmentOwnerClassOf, } from './overlay-attachment-owner-types.js';
// Sprint 63.26.0 (A5) — the one list of sample-data module keys.
export { SAMPLE_DATA_MODULES, SAMPLE_DATA_MODULE_KEYS, SAMPLE_DATA_MODULE_TO_MODULE_ID, SAMPLE_DATA_DEMO_SEED_MODULES, SAMPLE_DATA_ROW_TAG, isSampleDataModule, isSampleDataRow, sampleDataModuleForModuleId, } from './sample-data-modules.js';
// Sprint 63.37.0 (E0) — the organisation identity's field list, once.
export { ORG_IDENTITY_FIELD_DEFINITIONS, ORG_IDENTITY_FIELDS, ORG_BRANDING_TO_IDENTITY, ORG_IDENTITY_DATE_PATTERN, ORG_IDENTITY_PHONE_PATTERN, isOrgIdentityDate, } from './org-identity.js';
// Sprint 63.37.0 (E1, #617) — the closed registry of form prefill sources.
export { FORM_PREFILL_SOURCES, FORM_PREFILL_SOURCE_NAMES, FORM_PREFILL_SOURCE_FAMILIES, FORM_PREFILL_IDENTITY_SOURCES, getFormPrefillSource, formPrefillSourceAppliesTo, formPrefillModuleOf, } from './form-prefill-sources.js';
// fix-sprint-20260905-2135-followup-2 residue (run 10) — authority-filing
// vocabulary for new writes, and the ONE place the legacy stored spellings live.
export { BREACH_NOTIFICATION_SUBMITTED_STATUS, LEGACY_BREACH_NOTIFICATION_STATUS_ALIASES, AUTHORITY_FILING_AUDIT_ACTIONS, LEGACY_AUTHORITY_FILING_AUDIT_ACTION_ALIASES, canonicalBreachNotificationStatus, breachNotificationStatusSpellings, auditActionSpellings, } from './authority-filing-legacy-vocabulary.js';
export * from './background-request.js';
// Sprint 62.50.1 — the derived-state words the supervision-information register
// pages and their exports share (one copy; the pages' strings read it).
export { PCF_SUPERVISION_WORDS, pcfSupervisionWord, } from './pcf-supervision-words.js';
// Sprint 62.55.0 (UC-VN-109 F-1, F-3) — the words the fee-line exports and the
// Fee Lines by Period page share (one copy).
export { REGULATORY_FEE_WORDS, regulatoryFeeWord, } from './regulatory-fee-words.js';
// Sprint 62.51.0 — the words the data-exploitation pages and exports print for a derived state.
export { DATA_EXPLOITATION_WORDS, dataExploitationWord, } from './data-exploitation-words.js';
// Sprint 62.57.0 Slice E (B-dev-8) — the words the processing service certificate register's exports print.
export { SERVICE_CERTIFICATE_WORDS, SERVICE_CERTIFICATE_CODE_WORDS, serviceCertificateWord, serviceCertificateCodeWord, } from './service-certificate-words.js';
// Sprint 62.52.0 — the words the cybersecurity assurance index pages and export print for a derived state.
export { CYBERSECURITY_ASSURANCE_INDEX_WORDS, cybersecurityAssuranceIndexWord, } from './cybersecurity-assurance-index-words.js';
// Sprint 62.49.0 — the words the food traceability pages, exports and internal report copy print for a derived state.
export { FOOD_TRACEABILITY_WORDS, foodTraceabilityWord, } from './food-traceability-words.js';
//# sourceMappingURL=index.js.map